import { useEffect, useRef, useState } from 'react';
import { Select } from '@chakra-ui/react';
import { embedDashboard } from '@superset-ui/embedded-sdk';

type FilterOptions = {
    year?: number;
};

type RlsOptions = {
    clause: string;
    dataset: number;
};

const SUPERSET_BASE = 'https://athena.focusonesoftware.com';
const SUPERSET_PASS = 'AG1q2w3e4r';
const SUPERSET_ADMIN = 'embedded';

const DMS_DASHBOARD_UUID = '5f4ba0a3-4238-412a-9467-eb841e42c523';

async function login() {
    const response = await fetch(`${SUPERSET_BASE}/api/v1/security/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            provider: 'db',
            username: SUPERSET_ADMIN,
            password: SUPERSET_PASS,
            refresh: true
        })
    });
    if (!response.ok) {
        console.error('Login failed', await response.json());
        throw new Error('Login failed');
    }

    const data = await response.json();
    return data;
}

async function createCSRFToken(accessToken: string) {
    const uri = `${SUPERSET_BASE}/api/v1/security/csrf_token/`;

    const response = await fetch(uri, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
        }
    });
    if (!response.ok) {
        throw new Error('Failed to create CSRF token');
    }

    const data = await response.json();
    return {
        csrfToken: data.result,
        referer: uri
    };
}

async function createGuestToken(uuid: string, filterOptions?: FilterOptions) {
    const { access_token } = await login();
    const { csrfToken, referer } = await createCSRFToken(access_token);

    const clauses: RlsOptions[] = [];
    if (filterOptions?.year) {
        const { year } = filterOptions;
        clauses.push({
            clause: `timestamp >= '${year}0101' and timestamp <= '${year}1231'`,
            dataset: 30
        });
    }

    const response = await fetch(`${SUPERSET_BASE}/api/v1/security/guest_token/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${access_token}`,
            'X-CSRFToken': csrfToken,
            Referer: referer
        },
        body: JSON.stringify({
            resources: [
                {
                    type: 'dashboard',
                    id: uuid
                }
            ],
            rls: clauses,
            user: {
                username: 'embedded',
                first_name: 'embedded',
                last_name: 'embedded'
            }
        })
    });
    if (!response.ok) {
        throw new Error(JSON.stringify(await response.json()));
    }

    const data = await response.json();
    return data.token;
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 2023;

const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - MIN_YEAR + 1 }).map(
    (_, index) => CURRENT_YEAR - index
);

export default function DMSDashboard() {
    const dashboardContainerRef = useRef<HTMLDivElement>(null);

    const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);

    useEffect(() => {
        const containerDashboard = dashboardContainerRef.current;
        if (!containerDashboard) return;

        embedDashboard({
            id: DMS_DASHBOARD_UUID,
            supersetDomain: SUPERSET_BASE,
            mountPoint: containerDashboard,
            fetchGuestToken: () => createGuestToken(DMS_DASHBOARD_UUID, { year: selectedYear }),
            dashboardUiConfig: {
                hideTitle: true,
                hideTab: true,
                hideChartControls: true,
                urlParams: {
                    // native_filters: `(NATIVE_FILTER-${metaId}:(__cache:(label:'${value}',validateStatus:!f,value:!('${value}')),extraFormData:(filters:!((col:${column},op:IN,val:!('${value}')))),filterState:(label:'${value}',validateStatus:!f,value:!('${value}')),id:NATIVE_FILTER-${metaId},ownState:()))`
                },
                filters: {
                    expanded: false,
                    visible: false
                }
            }
        });
    }, [selectedYear]);

    return (
        <div className="mt-2 flex flex-1 flex-col gap-4">
            <div className="flex items-center gap-2 self-end">
                <span className="font-semibold">Year</span>
                <Select
                    value={selectedYear}
                    onChange={event => setSelectedYear(Number(event.target.value))}
                >
                    {YEAR_OPTIONS.map(year => (
                        <option
                            key={year}
                            value={year}
                        >
                            {year}
                        </option>
                    ))}
                </Select>
            </div>
            <div
                ref={dashboardContainerRef}
                className="flex flex-1 flex-col [&_iframe]:flex-1 [&_iframe]:rounded-lg [&_iframe]:border-none"
            />
        </div>
    );
}
